import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost } from "@vimaana/ui";

import type { Dokument } from "../components/DokumentLightbox";
import type { Packzettel, PackzettelCreate } from "../lib/packzettel";
import { seitenbilderAus } from "../lib/seitenbilder";

/** Die Packzettel eines Angebots — über beide Bezüge (Entwurf und Beleg). */
export function usePackzettel(entwurfId?: string, rechnungId?: string) {
  const frage = new URLSearchParams();
  if (entwurfId) frage.set("entwurf_id", entwurfId);
  if (rechnungId) frage.set("rechnung_id", rechnungId);
  return useQuery<{ items: Packzettel[] }>({
    queryKey: ["packzettel", entwurfId || "", rechnungId || ""],
    queryFn: () => apiGet(`/api/kontor/rechnungen/packzettel?${frage.toString()}`),
    enabled: Boolean(entwurfId || rechnungId),
    staleTime: 30_000,
  });
}

export function useCreatePackzettel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PackzettelCreate) =>
      apiPost<Packzettel>("/api/kontor/rechnungen/packzettel", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["packzettel"] }),
  });
}

export function useDeletePackzettel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiDelete<{ ok: boolean }>(`/api/kontor/rechnungen/packzettel/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["packzettel"] }),
  });
}

/**
 * Den Packzettel für die Dokumentansicht holen — als Seitenbilder.
 *
 * Nicht die Route direkt verlinken: der PDF-Handler des Browsers lud in
 * Firefox/Linux herunter statt anzuzeigen, ein `<iframe src="…/pdf">`
 * scheitert an `X-Frame-Options`, und ein neuer Tab mit `blob:`-Adresse
 * blieb auf dem Telefon leer. Der Zettel wird bei jedem Abruf neu erzeugt,
 * hat also keine feste Seitenadresse — deshalb geht das PDF einmal zum
 * Server und kommt als Bilder zurück. `url` (blob) bleibt für „PDF".
 * Wer den Dokument-Eintrag schließt, gibt `url` mit `URL.revokeObjectURL` frei.
 */
export async function packzettelDokument(id: string, nummer: string): Promise<NonNullable<Dokument>> {
  const antwort = await fetch(`/api/kontor/rechnungen/packzettel/${id}/pdf`, {
    credentials: "include",
  });
  if (!antwort.ok) throw new Error("Das PDF konnte nicht erzeugt werden.");
  const blob = await antwort.blob();
  const seitenBilder = await seitenbilderAus(blob);
  return { titel: `Packzettel ${nummer}`.trim(), url: URL.createObjectURL(blob), seitenBilder };
}
